import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';

import * as I from 'interfaces';

import {type NavigationTreePlan, type NavigationTreeSite, type WriteFallowing} from 'client';
import {type AxiosError} from 'axios';
import Alert from 'helpers/Alert';
import {localDateSender} from 'helpers/UnitsFormatHandler';
import {mutateCage, mutateSite} from 'helpers/refreshHandler';
import {type TFunction} from 'i18next';
import {type Dispatch, type MutableRefObject, type SetStateAction} from 'react';

const siteFallowingToSend = (
  fallowing: I.FallowingInitialState,
  setCreateUpdateSiteFallowing: Dispatch<SetStateAction<boolean>>,
  t: TFunction,
): WriteFallowing | undefined => {
  if (fallowing.startDate && fallowing.endDate && fallowing.startDate.getTime() >= fallowing.endDate.getTime()) {
    Alert.warning(t('startDateEndDateError'));
    setCreateUpdateSiteFallowing(false);
  } else if (fallowing.siteId === null) {
    Alert.warning(t('noSiteError'));
    setCreateUpdateSiteFallowing(false);
  } else {
    return {
      siteId: fallowing.siteId,
      startDate: localDateSender(fallowing.startDate),
      endDate: localDateSender(fallowing.endDate),
    };
  }
};

export const handleCommunicationSuccess = ({
  setCreateUpdateSiteFallowing,
  setSiteFallowing,
  siteId,
  treePlan,
}: IHandleCommunicationSuccess) => {
  setCreateUpdateSiteFallowing(false);
  setSiteFallowing(undefined);
  void mutate([siteId, I.SWRKeyEnum.GET_SITE_FALLOWINGS]);
  mutateSite(siteId);

  const treeSite: NavigationTreeSite | undefined = treePlan.sites?.find((site) => site.id === siteId);
  treeSite?.cages?.forEach((cages) => {
    mutateCage(cages.id);
  });
};

const backendOperator = async (
  method: I.methodType,
  siteFallowing: WriteFallowing,
  t: TFunction,
  fallowingId?: string,
) => {
  if (method === I.methodType.edit) {
    if (fallowingId) {
      return await backendCommunicator.updateSiteFallowing(fallowingId, siteFallowing);
    }
    Alert.warning(t('noFallowingError'));
  } else if (method === I.methodType.add) {
    return await backendCommunicator.addSiteFallowing(siteFallowing);
  }
};

const addEditFallowingBackendHandler = async ({
  siteFallowing,
  method,
  createUpdateSiteFallowing,
  setCreateUpdateSiteFallowing,
  inputMenuDispatch,
  fallowingId,
  communicationSuccess,
  isMounted,
  t,
}: IHandleCommunication) => {
  const _siteFallowing =
    createUpdateSiteFallowing && siteFallowingToSend(siteFallowing, setCreateUpdateSiteFallowing, t);
  const backendOperation = _siteFallowing && backendOperator(method, _siteFallowing, t, fallowingId);

  const response =
    backendOperation &&
    backendOperation
      .then((response) => {
        isMounted.current && communicationSuccess();
        return response;
      })
      .catch((error: AxiosError) => {
        isMounted.current && Alert.error(error);
      })
      .finally(() => {
        if (isMounted.current && inputMenuDispatch) {
          setCreateUpdateSiteFallowing(false);
          inputMenuDispatch({type: I.inputMenuActionType.closeMenu});
        }
      });
  return await response;
};

export default addEditFallowingBackendHandler;

interface IHandleCommunication {
  setCreateUpdateSiteFallowing: Dispatch<SetStateAction<boolean>>;
  inputMenuDispatch?: Dispatch<I.InputMenuAction>;
  method: I.methodType;
  siteFallowing: I.FallowingInitialState;
  createUpdateSiteFallowing: boolean;
  fallowingId?: string;
  communicationSuccess: () => void;
  isMounted: MutableRefObject<boolean>;
  t: TFunction;
}

interface IHandleCommunicationSuccess {
  setCreateUpdateSiteFallowing: Dispatch<SetStateAction<boolean>>;
  setSiteFallowing: Dispatch<SetStateAction<I.FallowingInitialState | undefined>>;
  inputMenuDispatch?: Dispatch<I.InputMenuAction>;
  planId: string;
  siteId: string;
  treePlan: NavigationTreePlan;
}
